import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation, useSearchParams } from "react-router";
import type { CropInfo, HomeSnapshot, PlotSummary } from "@/shared/contracts";
import { newIdempotencyKey } from "@/shared/api/idempotency";
import { toApiError } from "@/shared/api/errors";
import { env } from "@/shared/config/env";
import { queryKeys } from "@/shared/query/queryClient";
import { useServices } from "@/shared/services/registry";
import { useActiveHome, useOptionalCurrentHousehold } from "@/shared/session/householdContext";
import { formatDuration, useNow } from "@/shared/time/clock";
import { Button, ErrorState, Icon, Page, PetAvatar, QueryView, Sheet } from "@/shared/ui";
import { cropArt, isKnownCrop, produceVisual, SEED_PACKET_ATLAS, SEED_PACKET_CELLS } from "./cropVisual";
import { BasketCatch, FARM_ART, SeedPour } from "./FarmMotion";
import { GARDEN_PLOT_STATE_ART, GARDEN_REFERENCE060R1, GardenBed, GardenScene, gardenSlot, gardenGridStyle } from "./GardenScene";
import { forcedSky, SKY_PARAM, skyOf } from "@/features/home/homeStage";
import { WATCH_TONE, levelFromGuard, ownerWatchText, watchShort } from "./guardCopy";
import { GardenTools, useGardenCommand, type GardenTool, type GardenCommandScope } from "./GardenTools";
import {createFarmUnlockJournal,FARM_JOURNAL_KEY,farmScopeKey,quoteKey,withFarmAdmission,type FarmIdentity,type FarmScope,type UnlockQuote} from './farmUnlockJournal';
import {confirmFarm,readFarmPlan,planQuote,qualifiedUnlockReceipt,recoveredUnlock,type FarmPlan,type UnlockReceipt} from './farmExpansion.service';
import "./farm.css";

/*
 * 菜园（二级页 /garden）：从家园的“菜园”入口进来。QQ 农场式——整页是一块场地，点地里的菜就是操作：
 * 点地块看状态，再选择收获或种植。仓库/集市与串门从场景侧边去；播种沿当前合法空地的明确意图。
 * 规则都由服务端裁决：成熟按服务端时间、收成先进仓库（不直接加旅费）、稀有作物消耗旅行带回的种子。
 */

/**
 * 正在长的还要多久（点一下冒的泡泡，前面带一个空格接在作物名后面）：剩 1 分钟以内——包括已经过点、服务端还没改成熟了——说“快熟了”，
 * 不写“还要 0 分钟”（2026-09-27：剩 ≤ 60 秒）。作物熟没熟和旅途到没到是两件事：旅途那边 10-13 起统一说“预计还要…”，这里照旧说“快熟了”。其余照旧四舍五入到分钟。
 */
export function ripeLeftText(left: number | null): string {
  return left !== null && left > 60_000 ? ` 还要 ${formatDuration(left)}` : " 快熟了";
}

function growText(seconds: number): string {
  return seconds < 3600 ? `${Math.round(seconds / 60)} 分钟` : `${(seconds / 3600).toFixed(seconds % 3600 ? 1 : 0)} 小时`;
}

type FarmIntent = { plot: PlotSummary; action: "plant" | "harvest"; cropKey?: string };
function useFarmAction(homeId: string, scope: GardenCommandScope, accepted: (input: FarmIntent) => void) {
  const { farm } = useServices();
  const queryClient = useQueryClient();
  const gate = useGardenCommand(scope, "farm", (input: FarmIntent, key) => {
      const body = { home_id: homeId, plot_id: input.plot.plot_id, cycle_id: input.plot.cycle_id, action: input.action, crop_key: input.cropKey ?? null };
      return farm.act(body, key);
    }, (_result, input) => {
      const [userId,, ,petId]=scope;
      void queryClient.invalidateQueries({ predicate:q=>q.queryKey[0]===queryKeys.home[0]&&q.queryKey[1]===queryKeys.home[1]&&(env.dataMode==="fixture"?q.queryKey.length===2:q.queryKey[2]===userId&&q.queryKey[3]===petId) });
      void queryClient.invalidateQueries({ queryKey:env.dataMode==="fixture"?queryKeys.collection:queryKeys.collectionFor(userId??"-",petId??"-") });
      accepted(input);
  }, input=>JSON.stringify({home_id:homeId,plot_id:input.plot.plot_id,cycle_id:input.plot.cycle_id,action:input.action,crop_key:input.cropKey??null}));
  return {...gate.mutation, variables:gate.mutation.variables?.input, mutate:gate.mutate,held:gate.held,uncertain:gate.uncertain&&!gate.unlockUnknown,wasAccepted:gate.wasAccepted,isHeld:gate.isHeld,isActionHeld:gate.isActionHeld};
}

/**
 * UI060-R1 种子背包里的一个纸袋：从纸袋图集（材料包原字节）按 viewBox 取一格，PNG 不裁不改；名字、数量由按钮里的 DOM 字给。
 * 表外作物没有纸袋图：照旧用中性作物图，不冒充任何一种作物。
 */
function SeedPacket({ cropKey }: { cropKey: string | null }) {
  if (!isKnownCrop(cropKey)) return <img src={cropArt(cropKey, "ripe", "visual049").src} alt="" />;
  const [x, y, w, h] = SEED_PACKET_CELLS[cropKey];
  return <svg className="ps-seed-packet-r1" data-seed-packet={cropKey} viewBox={`${x} ${y} ${w} ${h}`} aria-hidden="true" focusable="false"><image href={SEED_PACKET_ATLAS.src} width={SEED_PACKET_ATLAS.width} height={SEED_PACKET_ATLAS.height} /></svg>;
}

/** UI060-R1 地块上的小木牌（只给眼睛看；按钮自己的读屏名照旧来自 myBedTag）：熟了“可收获”，在长“成长中”，空地“点地播种”。 */
function referenceTag(plot: PlotSummary): string {
  if (plot.stage === "ripe") return "可收获";
  if (plot.stage === "growing") return "成长中";
  return "点地播种";
}

function CropPicker({ onPick, onClose, busy, error, ready, plotFirst }: { onPick: (crop: CropInfo) => void; onClose: () => void; busy: boolean; error: unknown; ready: boolean; plotFirst: boolean }) {
  const { farm, economy } = useServices();
  const catalogClient = useQueryClient();
  const refreshScope = useRef<{ ready: boolean; busy: boolean } | null>(null);
  const refreshing = useRef(false);
  useLayoutEffect(() => {
    refreshScope.current = { ready, busy };
    return () => { refreshScope.current = null; };
  }, [ready, busy]);
  const selection = useOptionalCurrentHousehold();
  const userId = selection?.userId ?? null;
  const petId = selection?.pet?.pet_id ?? null;
  const fixture = env.dataMode === "fixture";
  const crops = useQuery({queryKey:queryKeys.crops,queryFn:()=>{
    if(typeof farm.crops!=="function")throw new Error("种子目录暂不可用");
    return farm.crops();
  },enabled:ready,retry:false,refetchOnWindowFocus:false,refetchOnReconnect:false,staleTime:10*60_000});
  const items = useQuery({queryKey:fixture?queryKeys.collection:queryKeys.collectionFor(userId??"-",petId??"-"),queryFn:({signal})=>{
    if(typeof economy.collection!=="function")throw new Error("种子库存暂不可用");
    return economy.collection(petId,signal);
  },enabled:ready&&(fixture||Boolean(userId&&petId)),retry:false,refetchOnWindowFocus:false,refetchOnReconnect:false});
  const refreshCatalog = async () => {
    if (!refreshScope.current?.ready || refreshScope.current.busy || refreshing.current || catalogClient.getQueryState(queryKeys.crops)?.fetchStatus === "fetching") return;
    refreshing.current = true;
    try { await crops.refetch({ cancelRefetch: false }); }
    finally { refreshing.current = false; }
  };
  const [sourceFilter,setSourceFilter]=useState<"all"|"common"|"travel">("all");
  const filtersRef=useRef<HTMLDivElement>(null);
  const visibleCrops=(crops.data??[]).filter(c=>sourceFilter==="all"||c.requires_seed===(sourceFilter==="travel"));
  const [detailKey,setDetailKey]=useState<string|null>(null);
  const detail=visibleCrops.find(c=>c.crop_key===detailKey)??visibleCrops[0];
  const currentCrops=crops.isSuccess&&!crops.isFetching&&!crops.isStale;
  const seeds=(key:string)=>(items.data??[]).filter(i=>i.kind==="seed"&&i.item_key===key).length;
  // UI060-R1（用户 10-08 参考图：点一块空地，就在那块地上方弹出“选种子”小卡片）：先点地再选种子时，背包贴着那块地（data-picking）显示，
  // 上方放不下就放在地的下方；从工具“种子袋”打开时照旧是贴底背包。只改位置与样式，内容、规则、回调不变。
  const probe=useRef<HTMLSpanElement>(null);
  useLayoutEffect(()=>{
    const sheet=probe.current?.closest<HTMLElement>(".ps-sheet");
    if(!sheet)return;
    if(!plotFirst){delete sheet.dataset.place;for(const name of ["--pop-w","--pop-left","--pop-top","--pop-arrow"])sheet.style.removeProperty(name);return;}
    const place=()=>{
      const soil=document.querySelector<HTMLElement>('[data-testid="garden-plot"][data-picking="true"] .ps-garden-bed__soil');
      if(!soil){delete sheet.dataset.place;return;}
      const r=soil.getBoundingClientRect(),vw=window.innerWidth,w=Math.min(320,vw-24),h=Math.min(sheet.offsetHeight,window.innerHeight-16);
      const cx=r.left+r.width/2,left=Math.max(12,Math.min(cx-w/2,vw-w-12)),anchorTop=r.top+r.height*0.3,anchorBottom=r.top+r.height*0.72;
      const above=anchorTop-h-12>=8;
      sheet.dataset.place=above?"above":"below";
      sheet.style.setProperty("--pop-w",`${w}px`);sheet.style.setProperty("--pop-left",`${left}px`);
      sheet.style.setProperty("--pop-top",`${Math.max(8,Math.min(above?anchorTop-h-12:anchorBottom+12,window.innerHeight-h-8))}px`);
      sheet.style.setProperty("--pop-arrow",`${Math.max(20,Math.min(cx-left,w-20))}px`);
    };
    place();window.addEventListener("resize",place);
    window.addEventListener("scroll",place,{capture:true,passive:true});
    const observer=typeof ResizeObserver==="undefined"?null:new ResizeObserver(place);
    observer?.observe(sheet);
    return()=>{window.removeEventListener("scroll",place,true);window.removeEventListener("resize",place);observer?.disconnect();};
  });
  return <Sheet className="ps-crop-sheet ps-seedbag051 ps-seedbag060 ps-seedbag-reference-r1 ps-seedbag-contact-stage" title="种子背包" subtitle={plotFirst?"选一袋种子，种在这块空地":"拿起种子，再点一块空地"} onClose={onClose}>
    <span ref={probe} hidden />
    {!ready?<p role="alert">当前身份尚未确认，暂不能播种。</p>:null}
    {ready&&crops.isPending?<p role="status">正在查看种子…</p>:null}
    {ready&&crops.isSuccess&&crops.isStale?<p role="status">{crops.isFetching?"正在刷新种子目录…":"种子目录已过期，请刷新后再选种子。"}<Button size="sm" disabled={busy||crops.isFetching} onClick={()=>void refreshCatalog()}>刷新种子目录</Button></p>:null}
    {crops.isError?<p role="alert">种子目录暂不可用。<Button size="sm" disabled={!ready||busy||crops.isFetching} onClick={()=>void refreshCatalog()}>重新查看</Button></p>:null}
    {ready&&crops.isError&&crops.isFetching?<p role="status">正在刷新种子目录…</p>:null}
    {items.isError?<p role="alert">种子数量暂未确认，不能使用旅行种子。</p>:null}
    <div ref={filtersRef} className="ps-seedbag-source-filter" role="group" aria-label="种子来源">
      {([["all","全部"],["common","常备"],["travel","旅行"]] as const).map(([key,label])=><Button key={key} variant="ghost" size="sm" data-source={key} aria-pressed={sourceFilter===key} onClick={()=>setSourceFilter(key)}>{label}</Button>)}
    </div>
    <div className="ps-seedbag051__grid" aria-label="种子格">
      {visibleCrops.map(crop=>{
        const known=items.isSuccess&&!items.isFetching;
        const count=crop.requires_seed?(known?seeds(crop.crop_key):null):null;
        return <button type="button" className="ps-seedbag051__slot" key={crop.crop_key} aria-label={`选择${crop.label}`} aria-describedby={detail?.crop_key===crop.crop_key?"seed-detail051":undefined} data-detail={detail?.crop_key===crop.crop_key} disabled={!ready||busy||!currentCrops||(crop.requires_seed&&(!known||count===0))} onFocus={()=>setDetailKey(crop.crop_key)} onMouseEnter={()=>setDetailKey(crop.crop_key)} onClick={()=>onPick(crop)}>
          <SeedPacket cropKey={crop.crop_key}/>
          <strong>{crop.label}</strong><small>{crop.requires_seed?count===null?"待确认":`×${count}`:"常备种子"}</small>
          {plotFirst?<small className="ps-seedbag-r1__time">{growText(crop.grow_seconds)}</small>:null}
        </button>;
      })}
    </div>
    {ready&&currentCrops&&visibleCrops.length===0?<div className="ps-seedbag-source-filter__empty">
      <p role="status">{sourceFilter==="travel"?"暂时没有旅行种子。":sourceFilter==="common"?"暂时没有常备种子。":"暂时没有可选种子。"}</p>
      {sourceFilter!=="all"?<Button variant="ghost" size="sm" onClick={()=>{setSourceFilter("all");filtersRef.current?.querySelector<HTMLButtonElement>('[data-source="all"]')?.focus({preventScroll:true});}}>返回全部种子</Button>:null}
    </div>:null}
    {detail?<details className="ps-seedbag051__detail" id="seed-detail051"><summary>{detail.label} · {growText(detail.grow_seconds)}成熟</summary><p>收 {detail.yield_units} 个 · 杂货铺约 {detail.yield_units*detail.unit_value} 星币</p><small>{detail.requires_seed?"消耗一份当前宠物旅行带回的种子":"常备种子，家里就有，不用出门找"} · <span className="ps-seedbag-d1__nowrap">邻里合计可摘 {detail.steal_total}</span></small></details>:null}
    {error?<ErrorState error={error}/>:null}
  </Sheet>;
}

/** 自家地里那块小木牌：作物 · 状态。 */
export function myBedTag(plot: PlotSummary): { text: string; tone: "ripe" | "quiet" } {
  if (plot.stage === "ripe") return { text: `${plot.crop_label ?? "作物"} · 可收获`, tone: "ripe" };
  if (plot.stage === "growing") return { text: `${plot.crop_label ?? "作物"} · 正在长`, tone: "quiet" };
  return { text: plot.stage === "harvested" ? "空出来啦 · 种点什么" : "空地 · 种点什么", tone: "quiet" };
}

/** Selected-plot DTO fact only; unknown or invalid quotas never become an invented number. */
function selectedQuotaText(plot: PlotSummary): string {
  if (plot.stage !== "growing" && plot.stage !== "ripe") return "";
  const remaining = plot.steal_remaining;
  const total = plot.steal_total;
  if (typeof remaining !== "number" || typeof total !== "number" || !Number.isSafeInteger(remaining) || !Number.isSafeInteger(total) || remaining < 0 || total < 0 || remaining > total) return "";
  return ` · 邻里可摘 ${remaining}/${total}`;
}

interface Flight {
  id: number;
  cropKey: string | null;
  from: { x: number; y: number };
  dx: number;
  dy: number;
}

/** Direct native planning and durable original-operation recovery. Home expansion is not a quote. */
function useFarmUnlock(snapshot:HomeSnapshot,userId:string|null,householdId:string|null,memberReady:boolean,externalHeld:()=>boolean){
 const services=useServices(),client=useQueryClient(),journal=useMemo(()=>createFarmUnlockJournal(),[]);
 const identity:FarmIdentity|null=userId&&householdId&&memberReady?{userId,householdId,homeId:snapshot.home_id,petId:snapshot.pet.pet_id}:null;
 const life=useMemo(()=>({alive:true,epoch:0,busy:false,reading:false,reread:false,controller:new AbortController()}),[userId,householdId,snapshot.home_id,snapshot.pet.pet_id,services]);
 const [view,setView]=useState<{epoch:number;scope:FarmScope;plan:FarmPlan;home:HomeSnapshot}|null>(null),[busy,setBusy]=useState(false),[notice,setNotice]=useState(''),[error,setError]=useState<unknown>(null),[receipt,setReceipt]=useState<{epoch:number;value:UnlockReceipt}|null>(null),[tick,setTick]=useState(0);
 const shown=life.alive&&view?.epoch===life.epoch?view:null;
 const stored=shown?journal.inspect(shown.scope):null;
 void tick;
 const held=journal.blocked(snapshot.home_id),uncertain=held&&!busy;
 const start=()=>{life.controller.abort();life.controller=new AbortController();const epoch=++life.epoch;return{signal:life.controller.signal,current:()=>life.alive&&life.epoch===epoch&&!life.controller.signal.aborted,epoch};};
 const clear=()=>{life.epoch++;life.controller.abort();setView(null);setReceipt(null);};
 const accept=(fresh:{scope:FarmScope;plan:FarmPlan;home:HomeSnapshot},epoch:number)=>{setView({...fresh,epoch});client.setQueryData(queryKeys.homeFor(fresh.scope.userId,fresh.scope.petId),fresh.home);};
 const refresh=async()=>{
  if(!life.alive||!identity||!services.farm.expansion)return;
  // StrictMode can replay setup while the aborted initial read is still settling: queue that read.
  // A pending money command never schedules another action; a plan re-read asked for meanwhile (identity changed, C2) runs after it ends.
  if(life.busy){life.reread=true;return;}
  life.busy=true;life.reading=true;setBusy(true);setError(null);setView(null);setReceipt(null);const job=start();
  try{const fresh=await readFarmPlan(identity,services,job.signal,job.current);if(job.current()){accept(fresh,job.epoch);setNotice('');}}
  catch(e){if(job.current()){setView(null);setReceipt(null);setError(e);}}
  finally{
   life.busy=false;life.reading=false;
   const reread=life.reread;life.reread=false;
   if(life.alive){setBusy(false);if(reread)void refresh();}
  }
 };
 useLayoutEffect(()=>{life.alive=memberReady;return()=>{life.alive=false;life.epoch++;life.controller.abort();};},[life,memberReady]);
 useEffect(()=>{
  const storage=(e:StorageEvent)=>{if(e.key===FARM_JOURNAL_KEY||e.key===null)setTick(v=>v+1);};window.addEventListener('storage',storage);
  const authorityKeys=[queryKeys.session,queryKeys.households(userId??'-'),queryKeys.householdDetail(userId??'-',householdId??'-')].map(k=>JSON.stringify(k));
  // 身份相关的三份查询真的变了（数据或状态变了、或被移除）才清掉计划，清完马上按新身份重读（UI2-REVIEW110-01，6c2b 查出的 C2）。
  // 原来只要有事件就清、清了不读：切回页面时这几份过期重拉一次，菜园就停在 8 格占位，「重新查看地块」也藏着，只能刷新页面。拉取了但什么都没变时不动。
  const cache=client.getQueryCache(),state=(k:string)=>{const q=cache.find({queryKey:JSON.parse(k) as readonly unknown[]});return q?{data:q.state.data as unknown,status:q.state.status as string}:null;};
  const seen=new Map(authorityKeys.map(k=>[k,state(k)]));
  const unsubscribe=cache.subscribe(e=>{
   const key=JSON.stringify(e.query.queryKey);if(!['updated','removed'].includes(e.type)||!authorityKeys.includes(key))return;
   const now=e.type==='removed'?null:{data:e.query.state.data as unknown,status:e.query.state.status as string},was=seen.get(key)??null;seen.set(key,now);
   if(now&&was&&now.data===was.data&&now.status===was.status)return;
   clear();void refresh();
  });
  void refresh();return()=>{window.removeEventListener('storage',storage);unsubscribe();};
 },[life,memberReady]);
 const submit=async(confirmed:UnlockQuote)=>{
  if(life.busy||!life.alive||!shown||!identity||!services.farm.unlock||!services.farm.unlockOperation||held||externalHeld())return;
  const scope=shown.scope;life.busy=true;setBusy(true);setError(null);setReceipt(null);const job=start();setView({...shown,epoch:job.epoch});
  try{await withFarmAdmission(async()=>{
   if(!job.current()||journal.inspect(scope).kind!=='clear'||externalHeld())return;
   const fresh=await readFarmPlan(identity,services,job.signal,job.current,scope),quote=planQuote(fresh.plan,confirmed.slot);
   if(!job.current())return;
   accept(fresh,job.epoch);
   if(!quote||quoteKey(quote)!==quoteKey(confirmed)){setNotice('开垦价格或条件已更新，请查看后重新确认。');return;}
   const final=await confirmFarm(identity,services,job.signal,job.current);
   if(!job.current()||farmScopeKey(final.scope)!==farmScopeKey(scope)||externalHeld()||journal.inspect(scope).kind!=='clear')throw Error('当前照顾资格尚未确认');
   const p=journal.begin({scope,quote,body:{home_id:scope.homeId,slot:quote.slot,config_version:quote.version},operationId:newIdempotencyKey('farm-unlock'),phase:'pending'});setTick(v=>v+1);
   try{
    const raw=await services.farm.unlock!(p.body,p.operationId);
    const after=await confirmFarm(identity,services,job.signal,job.current);
    if(!job.current()||farmScopeKey(after.scope)!==farmScopeKey(scope))throw Error('当前照顾资格已变化');
    const value=qualifiedUnlockReceipt(raw,p);journal.finish(p,'committed');setTick(v=>v+1);setReceipt({epoch:job.epoch,value});setNotice('开垦回执已确认，正在读取当前地块和钱包。');
   }catch(e){const problem=toApiError(e);if(job.current()&&problem.kind==='http'&&(problem.status===400||problem.status===422))journal.finish(p,'rejected');throw e;}
   // Original payer wallet/plot/expansion never fill the current pet's cache.
   const current=await readFarmPlan(identity,services,job.signal,job.current,scope);if(job.current()){accept(current,job.epoch);setNotice('当前地块和钱包已重新读取。');}
  });}catch(e){if(job.current()){setView(null);setError(e);setNotice('请重新查看当前菜园；未确认的开垦只会核对原操作。');setReceipt(null);}}
  finally{life.busy=false;const reread=life.reread;life.reread=false;if(life.alive){setBusy(false);setTick(v=>v+1);if(reread)void refresh();}}
 };
 const recover=async()=>{
  if(life.busy||!life.alive||!identity||!shown||stored?.kind!=='record'||!services.farm.unlockOperation||externalHeld())return;
  const p=stored.pending,scope=shown.scope;life.busy=true;setBusy(true);setError(null);setReceipt(null);const job=start();setView({...shown,epoch:job.epoch});
  try{await withFarmAdmission(async()=>{
   const before=await confirmFarm(identity,services,job.signal,job.current);if(farmScopeKey(before.scope)!==farmScopeKey(p.scope)||journal.inspect(scope).kind!=='record')throw Error('原开垦所属身份已变化');
   const raw=await services.farm.unlockOperation!(p.operationId,p.scope.homeId,job.signal),after=await confirmFarm(identity,services,job.signal,job.current);
   if(!job.current()||farmScopeKey(after.scope)!==farmScopeKey(p.scope))throw Error('原开垦所属身份已变化');
   const value=recoveredUnlock(raw,p);if(!value){setNotice('原开垦仍待确认，未再次提交。');return;}
   journal.finish(p,'committed');setTick(v=>v+1);setReceipt({epoch:job.epoch,value});
   const current=await readFarmPlan(identity,services,job.signal,job.current,scope);if(job.current()){accept(current,job.epoch);setNotice('原回执已确认，当前地块和钱包已重新读取。');}
  });}catch(e){if(job.current()){setView(null);setReceipt(null);setError(e);}}
  finally{life.busy=false;const reread=life.reread;life.reread=false;if(life.alive){setBusy(false);setTick(v=>v+1);if(reread)void refresh();}}
 };
 // 计划还在路上（有扩地能力、身份已确认、没出错、还没拿到）：页面先按 8 格占位，地块不突然变多（UI2-GARDEN-8-02）
 const planning=env.dataMode==='live'&&typeof services.farm.expansion==='function'&&Boolean(identity)&&!shown&&!error;
 return{readonlyUnknown:env.dataMode==='live'&&typeof services.farm.expansion==='function'&&!shown,planning,plan:shown?.plan,home:shown?.home,held,uncertain,pending:busy,submit,recover,refresh,notice,error,receipt:receipt?.epoch===life.epoch?receipt.value:null,recoverable:stored?.kind==='record',quarantined:stored?.kind==='held'};
}

/** Decorative outline in the soil plane; the existing button owns interaction and focus. */
function PlotBoundary() {
  return (
    <svg className="ps-garden-grid__boundary" viewBox="0 0 100 60" preserveAspectRatio="none" aria-hidden="true" focusable="false">
      <path d="M50 2 L98 30 L50 58 L2 30 Z" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

export function MyGarden(props: { snapshot: HomeSnapshot }) {
  const selection = useOptionalCurrentHousehold();
  const identity = JSON.stringify([selection?.userId, selection?.household?.household_id, props.snapshot.home_id, props.snapshot.pet.pet_id]);
  return <MyGardenBody key={identity} {...props} />;
}

/** 两份 /home 的地块是不是同一批（块数一样、编号都对得上；UI2-GARDEN-STALE-HOME-01） */
function samePlots(a: HomeSnapshot, b: HomeSnapshot): boolean {
  return a.plots.length === b.plots.length && b.plots.every((plot) => a.plots.some((p) => p.plot_id === plot.plot_id));
}

function MyGardenBody({ snapshot: incoming }: { snapshot: HomeSnapshot }) {
  const snapshotSeed = incoming;
  const layout = { ...GARDEN_REFERENCE060R1, src: "/ui-assets/UI-GARDEN-COMPACT-20261012/v1/garden-isometric.webp" };
  const queryClient = useQueryClient();
  const nowMs = useNow(1000);
  const { farm } = useServices();
  const selection = useOptionalCurrentHousehold();
  const memberReady = env.dataMode === "fixture" || Boolean(selection?.userId?.trim() && selection.pet?.pet_id === snapshotSeed.pet.pet_id && selection.household?.home_id === snapshotSeed.home_id && selection.household.household_id?.trim());
  const action = useFarmAction(snapshotSeed.home_id, [selection?.userId??null,selection?.household?.household_id??null,snapshotSeed.home_id,snapshotSeed.pet.pet_id], acceptedAction);
  const unlock = useFarmUnlock(snapshotSeed, selection?.userId ?? null, selection?.household?.household_id ?? null, memberReady, action.isActionHeld);
  // 画哪一份 /home（UI2-GARDEN-STALE-HOME-01，6c2b 查出的 C1）：unlock.home 只在读计划、开垦、回查、手点「重新查看地块」时更新。
  // 原来写的是 `unlock.home ?? incoming`，有八格计划时菜园就停在读计划那一刻：收完的菜还显示可收获，再点报「这块地的当前状态不能执行此操作。」，
  // 种下、钱包也不更新。现在地块是同一批时用父组件传来的新快照；对不上（刚开垦完、父组件还没重读，计划那份已是 4 块）才用它。
  const snapshot = unlock.home && !samePlots(unlock.home, incoming) ? unlock.home : incoming;
  // 父组件这份出现计划那份不知道的地块（别的家人刚开垦了）：自动重读一次计划，按新计划画，不停在旧的几块、等主人自己点「重新查看地块」
  // （UI2-GARDEN-PLAN-REFRESH-01，I 10-14 定）。同一批地块只读一次，读回来还是对不上也不追；地块一致、或计划那份多（刚开垦完、父组件还没重读）时不读。
  const planHome = unlock.home;
  const unknownPlot = Boolean(planHome) && incoming.plots.some((plot) => !planHome!.plots.some((p) => p.plot_id === plot.plot_id));
  const incomingPlots = incoming.plots.map((plot) => plot.plot_id).sort().join("|");
  const planReadFor = useRef<string | null>(null);
  const refreshPlan = unlock.refresh;
  useEffect(() => {
    if (!unknownPlot || planReadFor.current === incomingPlots) return;
    planReadFor.current = incomingPlots;
    void refreshPlan();
  }, [unknownPlot, incomingPlots, refreshPlan]);
  const canvasRef = useRef<HTMLDivElement>(null);
  const basketRef = useRef<HTMLAnchorElement>(null);
  const location = useLocation();
  const returnedFromMarket = location.state?.gardenReturnScope as unknown;
  useLayoutEffect(() => {
    const current = [selection?.userId, selection?.household?.household_id, snapshot.home_id, snapshot.pet.pet_id];
    if (!memberReady || !Array.isArray(returnedFromMarket) || returnedFromMarket.length !== current.length
      || current.some((id, index) => typeof id !== "string" || !id.trim() || returnedFromMarket[index] !== id)) return;
    // Explicit warehouse return only; current connected entrance, never override a focus the player has chosen.
    if (document.activeElement === document.body && basketRef.current?.isConnected) {
      basketRef.current.focus({ preventScroll: true });
    }
  }, [location.key, memberReady, returnedFromMarket, selection?.userId, selection?.household?.household_id, snapshot.home_id, snapshot.pet.pet_id]);

  const beds = useRef(new Map<string, HTMLDivElement>());
  const [pickingTarget, setPickingTarget] = useState<PlotSummary | null>(null);
  const pickingId=pickingTarget?.plot_id??null;
  const [intentProblem,setIntentProblem]=useState<string|null>(null);
  const [tool,setTool]=useState<GardenTool>("inspect");
  const [seed,setSeed]=useState<CropInfo|null>(null);
  const [bagOpen,setBagOpen]=useState(false);
  const picking = snapshot.plots.find(p => p.plot_id === pickingId && p.cycle_id===pickingTarget?.cycle_id && (p.stage === "empty" || p.stage === "harvested")) ?? null;
  useEffect(()=>{if(pickingId&&!picking){setPickingTarget(null);setBagOpen(false);}},[pickingId,picking]);
  const setPicking = (p: PlotSummary | null) => setPickingTarget(p);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = snapshot.plots.find(p => p.plot_id === selectedId) ?? null;
  const [selectedExpansion, setSelectedExpansion] = useState<number|null>(null);
  const selectionRef = useRef<HTMLElement>(null);
  const panelHeldFocus = useRef(false);
  const triggers = useRef(new Map<string, HTMLButtonElement>());
  const plantingFocus = useRef<string|null>(null);
  const plantingFallback = useRef(false);
  useEffect(()=>{
    if(!plantingFocus.current)return;
    const origin=triggers.current.get(plantingFocus.current);
    if(action.isPending){
      if((document.activeElement===document.body||(document.activeElement===origin&&origin?.disabled))&&basketRef.current?.isConnected){basketRef.current.focus({preventScroll:true});plantingFallback.current=true;}
      return;
    }
    if((document.activeElement===document.body||(plantingFallback.current&&document.activeElement===basketRef.current))&&origin?.isConnected&&!origin.disabled)origin.focus({preventScroll:true});
    plantingFallback.current=false;
    plantingFocus.current=null;
  },[action.isPending,action.isSuccess,action.isError,bagOpen]);
  const seedHandRef=useRef<HTMLDivElement>(null);
  const revokedSeedFocus=useRef(false);
  useEffect(()=>{
    if(!seed)return;
    const collectionKey=env.dataMode==="fixture"?queryKeys.collection:queryKeys.collectionFor(selection?.userId??"-",selection?.pet?.pet_id??"-");
    const keys=[JSON.stringify(queryKeys.crops),JSON.stringify(collectionKey)];
    let active=true;
    const check=()=>{
      if(!active)return;
      const cropState=queryClient.getQueryState(queryKeys.crops);
      const current=queryClient.getQueryData<CropInfo[]>(queryKeys.crops)?.find(c=>c.crop_key===seed.crop_key);
      let reason:string|null=null;
      if(!cropState||!current)reason="这份种子目前不在目录中，请重新查看种子背包。";
      else if(cropState.status!=="success")reason="种子目录尚未确认，请重新查看种子背包。";
      else if(cropState.fetchStatus!=="idle"||cropState.isInvalidated)reason="种子目录正在更新，请重新查看种子背包后再选。";
      else if(current.requires_seed!==seed.requires_seed)reason="这份种子的使用条件已变化，请重新查看种子背包。";
      else if(current.requires_seed){
        const items=queryClient.getQueryState(collectionKey);
        const data=queryClient.getQueryData<import("@/shared/contracts").CollectionItem[]>(collectionKey);
        if(!items||items.status!=="success"||items.fetchStatus!=="idle"||items.isInvalidated)reason="当前旅行种子数量尚未确认，请重新查看种子背包。";
        else if(!data?.some(i=>i.kind==="seed"&&i.item_key===current.crop_key))reason="这次选择的旅行种子库存已变化，请重新查看种子背包。";
      }
      if(!reason)return;
      if(seedHandRef.current?.contains(document.activeElement))revokedSeedFocus.current=true;
      setSeed(null);setTool(previous=>previous==="seed"?"inspect":previous);setIntentProblem(reason);
    };
    const unsubscribe=queryClient.getQueryCache().subscribe(event=>{if(keys.includes(JSON.stringify(event.query.queryKey)))check();});
    check();return()=>{active=false;unsubscribe();};
  },[seed,queryClient,selection?.userId,selection?.pet?.pet_id]);
  useEffect(()=>{
    if(seed||!revokedSeedFocus.current)return;
    if(document.activeElement===document.body){
      const bag=basketRef.current?.closest('.ps-my-garden')?.querySelector<HTMLButtonElement>('[aria-label="种子袋"]');
      (bag?.isConnected&&!bag.disabled?bag:basketRef.current)?.focus({preventScroll:true});
    }
    revokedSeedFocus.current=false;
  },[seed]);
  const expansion = unlock.plan;
  const lockedPlans = expansion?.slots.filter(s=>!s.opened)??[];
  // 计划回来之前：按 8 格把还没出现的格子先占上（和没开的地同一副样子，不能点、不挂牌），计划回来再换成真的；出错就撤掉，不猜
  const occupied = new Set(snapshot.plots.map((plot, index) => gardenSlot(snapshot.home_id, plot.plot_id, index)));
  const placeholderSlots = unlock.planning && !expansion ? [0, 1, 2, 3, 4, 5, 6, 7].filter(slot => !occupied.has(slot)) : [];
  const lockedSlot = selectedExpansion !== null && lockedPlans.some(s=>s.slot===selectedExpansion)?selectedExpansion:null;
  const lockedSelected = lockedSlot !== null;
  const quote = expansion&&lockedSlot!==null?planQuote(expansion,lockedSlot):null;
  const payerId = quote?.payerId;
  const payerName = (snapshot.pets ?? selection?.household?.pets ?? []).find(p=>p.pet_id===payerId)?.name ?? (payerId===snapshot.pet.pet_id?snapshot.pet.name:null);
  const canUnlock = memberReady && Boolean(quote) && typeof farm.unlock==='function' && typeof farm.unlockOperation==='function';
  // 余额不够（付钱的就是这只宠物、它的钱包就在本页）：按钮置灰，写还差多少（UI2-GARDEN-8-02；后端照样会拒，这里只是不让玩家白点）
  const shortBy = quote && payerId===snapshot.pet.pet_id && snapshot.wallet.balance<quote.cost ? quote.cost-snapshot.wallet.balance : 0;
  const unlockUnavailable = !canUnlock || unlock.held || action.held || shortBy>0;
  const unlockBlocking = unlock.pending || unlock.held || unlock.readonlyUnknown;
  // 没轮到的那块：说清楚要先开哪一块（UI2-GARDEN-8-01；地按顺序开，后端 next_slot 为准）
  const nextLocked = expansion?.nextSlot ?? null;
  // 排在后面的那块（UI2-REVIEW110-01，I 10-14：价格是让人攒钱的目标，不是收费墙）：说它排在哪一块后面、开垦要多少（后端 expansion.slots[].cost 给了才写，不编）
  const lockedCost = lockedSlot!==null ? lockedPlans.find(s=>s.slot===lockedSlot)?.cost ?? null : null;
  const queuedMessage = lockedSlot!==null ? (lockedCost!==null ? `排在第 ${lockedSlot} 块后面 · 开垦需要 ${lockedCost} 星币` : `排在第 ${lockedSlot} 块后面，轮到它时再开垦。`) : '';
  const expansionMessage = !memberReady?'正在确认家庭信息。':canUnlock?'开垦后，就能在这里种下新的作物。':lockedSlot!==null&&nextLocked!==null&&lockedSlot!==nextLocked?queuedMessage:'这块地暂时还不能开垦，先照顾其他地块吧。';
  useEffect(() => {
    if (selectedId !== null || selectedExpansion !== null) selectionRef.current?.focus();
  }, [selectedId, selectedExpansion]);
  useEffect(()=>{if(panelHeldFocus.current&&document.activeElement===document.body)(selectionRef.current??basketRef.current)?.focus({preventScroll:true});},[selected?.stage,seed,lockedSlot]);
  const inspect = (plot: PlotSummary) => {
    if(selectedId===plot.plot_id&&selectedExpansion === null){closeSelection();return;}
    setSelectedExpansion(null);setSelectedId(plot.plot_id);setPicking(null);setIntentProblem(null);
  };
  const closeSelection = () => {
    panelHeldFocus.current=false;
    const trigger = triggers.current.get(lockedSelected ? `expansion-${lockedSlot}` : selectedId ?? "");
    setSelectedId(null);
    setSelectedExpansion(null);
    (trigger?.isConnected && !trigger.disabled ? trigger : basketRef.current)?.focus({ preventScroll: true });
  };
  const [planted, setPlanted] = useState<string | null>(null);
  const [harvest, setHarvest] = useState<{ cropKey: string | null; n: number } | null>(null);
  const [flights, setFlights] = useState<Flight[]>([]);
  const flightTimers = useRef<ReturnType<typeof setTimeout>[]>([]);
  useEffect(() => () => flightTimers.current.forEach(clearTimeout), []);

  // 到了成熟时间就重新读取权威快照（成熟由服务端按时间判定）。
  const nextRipe = snapshot.plots.filter((p) => p.stage === "growing" && p.ripe_at).map((p) => Date.parse(p.ripe_at!)).sort((a, b) => a - b)[0];
  const due = nextRipe !== undefined && nextRipe <= nowMs;
  useEffect(() => {
    if (due) void queryClient.invalidateQueries({ queryKey: queryKeys.home });
  }, [due, queryClient]);
  useEffect(() => {
    if (!planted) return;
    const timer = window.setTimeout(() => setPlanted(null), 2600);
    return () => window.clearTimeout(timer);
  }, [planted]);

  // 一颗菜从那块地飞进右下角的篮子（画布坐标；篮子钉在场景角上，按实际位置算）。
  const flyToBasket = (plotId: string, cropKey: string | null) => {
    const canvas = canvasRef.current?.getBoundingClientRect();
    const bed = beds.current.get(plotId)?.getBoundingClientRect();
    const basket = basketRef.current?.getBoundingClientRect();
    if (!canvas || !bed || !basket) return;
    const from = { x: bed.left + bed.width / 2 - canvas.left, y: bed.top + bed.height * 0.2 - canvas.top };
    const to = { x: basket.left + basket.width / 2 - canvas.left, y: basket.top - canvas.top };
    const id = Date.now();
    setFlights((list) => [...list, { id, cropKey, from, dx: to.x - from.x, dy: to.y - from.y }]);
    // 飞完（animationend）就收走；减少动效时不播动画，靠兜底计时收走。
    flightTimers.current.push(setTimeout(() => landed(id), 3000));
  };
  const landed = (id: number) => setFlights((list) => list.filter((f) => f.id !== id));

  function acceptedAction({plot,action:kind}:FarmIntent) {
    if(kind==="harvest"){setHarvest(h=>({cropKey:plot.crop_key,n:(h?.n??0)+1}));flyToBasket(plot.plot_id,plot.crop_key);}
    else{setPicking(null);setPlanted(plot.plot_id);setSeed(null);setTool("inspect");setIntentProblem(null);}
  }
  const run = (plot: PlotSummary, kind: "plant" | "harvest", cropKey?: string) => {
    if(action.isHeld()||unlockBlocking)return false;
    const homeKey=env.dataMode==="fixture"?queryKeys.home:queryKeys.homeFor(selection?.userId??"-",snapshot.pet.pet_id);
    const homeState=queryClient.getQueryState(homeKey);
    if(homeState&&(homeState.status!=="success"||homeState.fetchStatus!=="idle"||homeState.isInvalidated)){setIntentProblem("地块状态尚未确认，请先重新查看菜园。");return false;}
    const fresh=queryClient.getQueryData<HomeSnapshot>(homeKey);
    const currentHome=fresh?.home_id===snapshot.home_id&&fresh.pet.pet_id===snapshot.pet.pet_id?fresh:snapshot;
    const current=currentHome.plots.find(p=>p.plot_id===plot.plot_id&&p.cycle_id===plot.cycle_id);
    if (!memberReady || !current || action.held || unlockBlocking || (kind==="harvest"&&(current.stage!=="ripe"||!current.cycle_id?.trim())) || (kind==="plant"&&current.stage!=="empty"&&current.stage!=="harvested")) {setIntentProblem("这块地的当前状态不能执行此操作。");return false;}
    if(kind==="plant") {
      const cropState=queryClient.getQueryState(queryKeys.crops);
      const crop=queryClient.getQueryData<CropInfo[]>(queryKeys.crops)?.find(c=>c.crop_key===cropKey);
      if(!crop||cropState?.status!=="success"||cropState.fetchStatus!=="idle"||cropState.isInvalidated){setIntentProblem("种子目录尚未确认，请重新查看。");return false;}
      if(crop.requires_seed) {
        const key=env.dataMode==="fixture"?queryKeys.collection:queryKeys.collectionFor(selection?.userId??"-",selection?.pet?.pet_id??"-");
        const items=queryClient.getQueryData<import("@/shared/contracts").CollectionItem[]>(key);
        const itemState=queryClient.getQueryState(key);
        if(itemState?.status!=="success"||itemState.fetchStatus!=="idle"||itemState.isInvalidated||!items?.some(i=>i.kind==="seed"&&i.item_key===crop.crop_key)){setIntentProblem("当前旅行种子尚未确认，请重新查看种子背包。");return false;}
      }
    }
    if((kind==="plant"&&bagOpen)||document.activeElement===triggers.current.get(current.plot_id))plantingFocus.current=current.plot_id;
    setIntentProblem(null);action.mutate({plot:current,action:kind,cropKey});return true;
  };
  const chooseGround=(plot:PlotSummary)=>{
    // Current ripe crop is the harvest trigger; run retains authority and unknown gates.
    if(plot.stage==="ripe"){run(plot,"harvest");return;}
    // UI-D3：种植在途时不开背包、不另选地；那块地的木牌显示“正在种…”（写入本来就只发一次，这里补上看得见的状态）
    if(action.held&&(plot.stage==="empty"||plot.stage==="harvested"))return;
    if((plot.stage==="empty"||plot.stage==="harvested")&&tool!=="inspect"&&tool!=="harvest"&&tool!=="hoe"&&seed){run(plot,"plant",seed.crop_key);return;}
    if((plot.stage==="empty"||plot.stage==="harvested")&&tool!=="harvest"&&tool!=="hoe"&&memberReady){setSelectedId(null);setSelectedExpansion(null);setPicking(plot);setBagOpen(true);setIntentProblem(null);return;}
    inspect(plot);
  };
  const closeBag=()=>{setBagOpen(false);setPicking(null);setSeed(null);setTool("inspect");};

  const done = action.isSuccess ? action.variables?.action : null;
  const pantryTotal = snapshot.pantry.reduce((n, item) => n + item.qty, 0);
  const watch = levelFromGuard(snapshot.guard);
  // 这只宠物此刻的 life（和小窝同一份快照里的 pets[]）。UI2-REVIEW110-01：
  // - 确实在睡觉：门牌、门边那句说“睡觉”不说“打盹”；
  // - 天色和小窝同一套（后端作息给的 sky；演示数据才认 ?sky=），夜里菜园切夜景，不再是半夜的蓝天白云。
  const petLife = (snapshot.pets ?? snapshot.household?.pets)?.find((p) => p.pet_id === snapshot.pet.pet_id)?.life ?? null;
  const asleep = petLife?.activity.kind === "sleeping";
  const [params] = useSearchParams();
  const sky = forcedSky({ dataMode: env.dataMode, dataOrigin: snapshot.data_origin, value: params.get(SKY_PARAM) }) ?? skyOf(petLife);

  const bed = (plot: PlotSummary, index: number) => {
    const tag = myBedTag(plot);
    const busy = action.isPending && action.variables?.plot.plot_id === plot.plot_id;
    const planting = action.held && action.variables?.plot.plot_id === plot.plot_id && (plot.stage === "empty" || plot.stage === "harvested");
    const slot = gardenSlot(snapshot.home_id, plot.plot_id, index);
    const label = `第 ${slot + 1} 块地：${tag.text}`;
    return (
      <div
        key={plot.plot_id}
        ref={(el) => {
          if (el) beds.current.set(plot.plot_id, el);
          else beds.current.delete(plot.plot_id);
        }}
        className={`ps-garden-slot ps-garden-grid__plot${busy ? " is-pending" : ""}`}
        style={gardenGridStyle(slot)}
        data-slot={slot}
        data-testid="garden-plot"
        data-plot-id={plot.plot_id}
        data-stage={plot.stage}
        data-selected={selected?.plot_id === plot.plot_id ? "true" : "false"}
        data-picking={pickingId === plot.plot_id ? "true" : undefined}
      >
        <button ref={(el) => { if (el) triggers.current.set(plot.plot_id, el); else triggers.current.delete(plot.plot_id); }} type="button" className="ps-garden-slot__hit" aria-label={label} aria-pressed={selected?.plot_id === plot.plot_id} aria-controls="garden-selection" aria-busy={busy} disabled={action.isPending || unlock.pending} onClick={() => chooseGround(plot)}>
          <GardenBed variant="reference060r1" cropKey={plot.crop_key} stage={plot.stage} planted={planted === plot.plot_id} />
          <PlotBoundary />
          <span className={`ps-garden-ref-r1__tag is-${planting ? "planting" : plot.stage}`} aria-hidden="true">{planting ? "正在种…" : referenceTag(plot)}</span>
          <span className="visually-hidden">{tag.text}{planting ? "，正在种" : ""}</span>
        </button>
        {planted === plot.plot_id ? <span className="ps-garden-slot__pour"><SeedPour /></span> : null}
        {busy && plot.stage === "ripe" ? <img className="ps-garden-slot__paw" src={FARM_ART.pickPaw} alt="" aria-hidden="true" data-testid="pick-paw" /> : null}
      </div>
    );
  };

  return (
    <div className="ps-stack ps-steal-page ps-my-garden" data-tool={tool} data-seed-armed={Boolean(seed&&tool==="seed"&&!action.held&&!unlockBlocking)} onClickCapture={e=>{const target=e.target as Element;if((selected||lockedSelected)&&!bagOpen&&!target.closest("button,a,#garden-selection,.ps-garden-feedback049")){panelHeldFocus.current=false;setSelectedId(null);setSelectedExpansion(null);}}} onKeyDown={e=>{if(e.key==="Escape"&&!bagOpen){e.preventDefault();if(selected||lockedSelected)closeSelection();else if(seed){setSeed(null);setTool("inspect");basketRef.current?.closest('.ps-my-garden')?.querySelector<HTMLButtonElement>('[aria-label="种子袋"]')?.focus();}}}}>
      <GardenScene
        variant="reference060r1"
        fence={false}
        sky={sky}
        layout={layout}
        label={`${snapshot.pet.name}的菜园`}
        canvasRef={canvasRef}
        hud={
          <>
            <header className="ps-garden-header047 ps-garden-header049"><Link to="/home?room=courtyard" aria-label="返回庭院"><Icon name="back" size={22}/></Link><h1>菜园</h1><span className="ps-garden-wallet" role="img" aria-label={`当前余额 ${snapshot.wallet.balance} 星币`}><Icon name="coin" size={16}/><strong>{snapshot.wallet.balance}</strong></span></header>
            <GardenTools variant="reference060r1" tool={tool} seedLabel={seed?.label} onTool={next=>{setTool(next);setIntentProblem(null);if(next==="seed"){setPicking(null);setBagOpen(true);}else{setSeed(null);setBagOpen(false);setPicking(null);}}}/>
            {seed&&tool==="seed"&&!action.held&&!unlockBlocking?<div ref={seedHandRef} className="ps-seed-hand051" role="status" aria-label="手里的种子"><img src={FARM_ART.seedBag} alt="" aria-hidden="true" draggable={false} /><span><strong>{seed.label}</strong><small>点一块空地，撒下种子</small></span><button type="button" aria-label="放回种子" onClick={()=>{setSeed(null);setTool("inspect");}}>×</button></div>:null}
            {/* 门牌只在有人守着时显示（用户 10-08：没人看着就不展示）；读屏仍有门边那句完整说明 */}
            <div className="ps-garden-scene__sign" data-watch={watch}>
              {/* 门牌昼夜都是固定暖纸（farm.css）：头像按纸面写，爪印、插画的底块深色下不变成纸上的深色块 */}
              <PetAvatar petId={snapshot.pet.pet_id} name={snapshot.pet.name} species={snapshot.pet.species} photoUrl={snapshot.pet.photo_url} origin={snapshot.pet.origin} surface="paper" size={32} />
              <span>
                <strong>{snapshot.pet.name}的菜园</strong>
                <small>{watchShort(watch, "owner", asleep)}</small>
              </span>
            </div>
            {/* 服务端没给开垦数据时不另外提示（用户 10-08 要求去掉左下“开垦信息”）：只是不出现可开垦的地，不猜总格数、价格或开放性 */}
            <Link ref={basketRef} onBlur={()=>{plantingFallback.current=false;}} onPointerDown={()=>{plantingFallback.current=false;}} to="/market" className="ps-garden-scene__basket is-link" aria-label={`仓库里有 ${pantryTotal} 个收成，去仓库与集市`}>
              {harvest ? <BasketCatch key={harvest.n} cropKey={harvest.cropKey} delayed /> : <img src={FARM_ART.basket} alt="" />}
              <b className="ps-garden-scene__count" aria-hidden="true">{pantryTotal}</b>
              {/* 看得见的“仓库 N”（用户 10-08：右下角的数字看不出是什么）；读屏名照旧是上面的 aria-label */}
              <span className="ps-garden-ref-r1__basket-label" aria-hidden="true">仓库 {pantryTotal}</span>
            </Link>
          </>
        }
      >
        {/* 与邻居页同一套“谁在看着”的说法：宠物在家是概率保护，不承诺绝对防偷 */}
        <p className={`ps-garden-bubble is-door ps-garden-watch060 is-${WATCH_TONE[watch]}`} data-testid="garden-watch">
          <Icon name={watch === "nobody" ? "sprout" : "alert"} size={14} />
          <span>{ownerWatchText(watch, snapshot.pet.name, snapshot.guard.until, asleep)}</span>
        </p>
        <div className="ps-garden-grid" role="group" aria-label="菜园地块">
          {snapshot.plots.map((plot, index) => bed(plot, index))}
          {placeholderSlots.map(slot => (
            <div className="ps-garden-grid__plot is-locked is-placeholder" key={`placeholder-${slot}`} data-testid="garden-plot-placeholder" data-slot={slot} data-stage="loading" aria-hidden="true" style={gardenGridStyle(slot)}>
              <span className="ps-garden-slot__hit">
                <span className="ps-garden-grid__grass" />
                <span className="ps-garden-grid__untilled-props">
                  <img className="ps-garden-grid__untilled-grass" src="/ui-assets/UI-GARDEN-WEBP-V2B-20261013/grass-clump.webp" alt="" draggable={false} />
                  <img className="ps-garden-grid__untilled-stones" src="/ui-assets/UI-GARDEN-WEBP-V2B-20261013/stone-cluster.webp" alt="" draggable={false} />
                </span>
                <PlotBoundary />
              </span>
            </div>
          ))}
          {lockedPlans.map(plan => (
            <div className="ps-garden-grid__plot is-locked" key={plan.plot_id} data-testid="garden-locked-plot" data-plot-id={plan.plot_id} data-slot={plan.slot} data-stage="locked" data-selected={lockedSlot===plan.slot ? "true" : "false"} style={gardenGridStyle(plan.slot)}>
              <button ref={(el) => { if (el) triggers.current.set(`expansion-${plan.slot}`, el); else triggers.current.delete(`expansion-${plan.slot}`); }} type="button" className="ps-garden-slot__hit" aria-label={`第 ${plan.slot + 1} 块地：未开垦，查看说明`} aria-pressed={lockedSlot===plan.slot} aria-controls="garden-selection" disabled={unlock.pending} onClick={() => {if(lockedSlot===plan.slot){closeSelection();return;}setSelectedId(null);setSelectedExpansion(plan.slot);setPicking(null);setBagOpen(false);}}>
                {/* UI2-GARDEN-2x4-NIGHT-117：r7k 的地块图（ASSET-UI2-GARDEN-PLOTS-02），排在下一块的画未开垦、后面几块画锁着；和已开垦的土块同一个框。
                    只装饰权威待开垦格，不改变开垦资格 */}
                <span className="ps-garden-grid__state" data-state={plan.next_in_order ? "untilled" : "locked"} aria-hidden="true">
                  <img className="ps-garden-grid__state-art" src={plan.next_in_order ? GARDEN_PLOT_STATE_ART.untilled : GARDEN_PLOT_STATE_ART.locked} alt="" draggable={false} />
                </span>
                <PlotBoundary />
                {/* 只有排在下一块（next_in_order）的挂一块浅色木牌；后面几块不挂牌。价格是让人攒钱的目标、不是收费墙（I 10-14，UI2-REVIEW110-01，
                    推翻 UI2-GARDEN-8-01 的“不写价格”）：后端给了价就写「待开垦 · 150 星币」，没给就照旧只写「待开垦」 */}
                {plan.next_in_order ? <span className="ps-garden-ref-r1__locktag is-next" aria-hidden="true">{plan.cost !== null ? `待开垦 · ${plan.cost} 星币` : "待开垦"}</span> : null}
                <span className="visually-hidden">未开垦</span>
              </button>
            </div>
          ))}
        </div>
        {flights.map((f) => (
          <span
            key={f.id}
            className="ps-garden-flyer"
            data-testid="garden-flyer"
            aria-hidden="true"
            style={{ left: f.from.x, top: f.from.y, "--dx": `${f.dx}px`, "--dy": `${f.dy}px` } as CSSProperties}
            onAnimationEnd={(e) => {
              if (e.target === e.currentTarget) landed(f.id);
            }}
          >
            <img src={produceVisual(f.cropKey)} alt="" />
          </span>
        ))}
      <div className="ps-garden-feedback049">
      {selected || lockedSelected ? (
        <section ref={selectionRef} tabIndex={-1} id="garden-selection" className="ps-garden-selection" aria-label="选中地块" data-plot-id={selected?.plot_id} onFocusCapture={()=>{panelHeldFocus.current=true;}} onBlurCapture={e=>{if(e.relatedTarget&&!e.currentTarget.contains(e.relatedTarget as Node))panelHeldFocus.current=false;}} onKeyDown={e => { if (e.key === "Escape" && !bagOpen) { e.preventDefault(); closeSelection(); } }}>
          <div className="ps-garden-selection__summary">
            <strong>{selected ? selected.crop_label ?? "空地" : "未开垦的地"}</strong>
            <p>{selected ? `${myBedTag(selected).text}${selected.stage === "growing" ? ripeLeftText(selected.ripe_at ? Date.parse(selected.ripe_at) - nowMs : null) : ""}${selectedQuotaText(selected)}` : expansionMessage}</p>
            {lockedSelected && canUnlock ? <p>开垦需要 {quote?.cost} 星币</p> : null}
            {lockedSelected && canUnlock ? <p>将从{payerName ? `${payerName}的账户` : "家庭宠物账户"}支付。</p> : null}
            {/* 新家地址还在落定时 TA 先待在家里、不出门（UI2-HOME-SMALL-01）：这时不说“出门打工也会带星币回来”（UI2-REVIEW110-01） */}
            {lockedSelected && canUnlock && shortBy>0 ? <p>现在有 {snapshot.wallet.balance} 星币，还差 {shortBy} 星币。卖菜能慢慢攒{snapshot.place?.address_pending ? "。" : "，TA 出门打工也会带星币回来。"}</p> : null}
          </div>
          <div className="ps-garden-selection__actions">
            {selected?.stage === "ripe" ? <Button variant="primary" size="sm" disabled={!memberReady || action.held || unlockBlocking} loading={action.isPending} onClick={() => run(selected, "harvest")}>收获{selected.crop_label ?? "作物"}</Button> : null}
            {selected && (selected.stage === "empty" || selected.stage === "harvested") ? <Button variant="primary" size="sm" disabled={!memberReady||action.held || unlockBlocking} onClick={() => {setPicking(selected);setBagOpen(true);setTool("seed");}}>选种子</Button> : null}
            {lockedSelected ? <Button variant="secondary" size="sm" disabled={unlockUnavailable || action.isPending} loading={unlock.pending} onClick={() => { if (canUnlock && !action.isPending && quote) void unlock.submit(quote); }}>{unlockUnavailable && !unlock.pending && shortBy===0 ? "暂时不能开垦" : "开垦这块地"}</Button> : null}
            <Button variant="ghost" size="sm" onClick={closeSelection}>收起详情</Button>
          </div>
        </section>
      ) : <div id="garden-selection" hidden />}
      {done === "harvest" && action.data?.gained_items.length ? (
        <p key={`h${harvest?.n ?? 0}`} className="ps-steal-receipt" role="status">
          {/* 服务端原话形如「星星番茄 ×6 进了仓库」 */}
          {action.data.gained_items.join("、")}。星币没有变，卖掉或交订单时才会增加。
        </p>
      ) : null}
      {done === "plant" ? (
        <p className="ps-steal-receipt" role="status">
          已种下{action.data?.plot.crop_label ?? "新的作物"}，等它慢慢长大。
        </p>
      ) : null}
      {intentProblem?<p className="ps-steal-problem" role="alert">{intentProblem}</p>:null}
      {action.uncertain ? <p className="ps-steal-problem" role="alert">这笔操作的结果还未确认，请勿重复提交。</p> : action.isError && !picking ? (
        <p className="ps-steal-problem" role="alert">
          {toApiError(action.error).playerMessage}
        </p>
      ) : null}
      {unlock.uncertain?<p className="ps-steal-problem" role="alert">开垦结果还未确认，不要重复开垦。</p>:null}
      {unlock.error?<p className="ps-steal-problem" role="alert">{toApiError(unlock.error).playerMessage}</p>:null}
      {unlock.notice?<p className="ps-steal-receipt" role="status">{unlock.notice}</p>:null}
      {unlock.receipt?<p className="ps-steal-receipt" role="status">原开垦支付 {unlock.receipt.paidCost} 星币，由家庭宠物账户支付。当前余额以重新读取的钱包为准。</p>:null}
      {env.dataMode==='live'&&typeof farm.expansion==='function'&&(unlock.error||unlock.held||(!expansion&&!unlock.planning))?<Button size="sm" variant="ghost" disabled={unlock.pending||action.isPending} onClick={()=>void unlock.refresh()}>重新查看地块</Button>:null}
      {unlock.recoverable?<Button size="sm" variant="secondary" disabled={unlock.pending||action.isPending} onClick={()=>void unlock.recover()}>核对原开垦</Button>:null}
      {unlock.quarantined?<p role="alert">原开垦属于之前的身份，请回到原账号和家庭核对。</p>:null}
      </div>
      </GardenScene>
      {bagOpen ? <CropPicker ready={memberReady} plotFirst={Boolean(picking)} busy={action.held || unlockBlocking} error={intentProblem?new Error(intentProblem):action.uncertain?null:action.error} onClose={closeBag} onPick={crop=>{
        if(pickingTarget&&!run(pickingTarget,"plant",crop.crop_key))return;
        if(!pickingTarget){setSeed(crop);setTool("seed");}
        setBagOpen(false);setPicking(null);
      }} /> : null}
    </div>
  );
}

export function GardenPage() {
  const query = useActiveHome({ refetchInterval: 60_000 });
  return (
    <Page className="ps-garden-page">
      {!query.data ? <header className="ps-garden-header047"><Link to="/home?room=courtyard" aria-label="返回庭院"><Icon name="back" size={22}/></Link><h1>菜园</h1></header> : null}
      {/* Explicit feature parent survives direct reload; shared SourceBack does not whitelist garden. */}
      <QueryView query={query}>{(snapshot) => <MyGarden snapshot={snapshot} />}</QueryView>
    </Page>
  );
}
